/**
 * 실시간 행 갱신 — 서버 이벤트(SSE)를 Dataset 에 키 기준으로 반영.
 *
 *   const sub = subscribeRows(ds, '/api/events?topic=waiting', { key: 'id', grid })
 *   sub.close()
 *
 * 이벤트 data(JSON): { type: 'add' | 'update' | 'remove', topic?, key?, rows: [{…}] | { n, data: {col: […]} } }
 *  - add/update 는 키로 찾아 있으면 갱신·없으면 추가 (upsert), remove 는 키로 제거
 *  - 저장 안 한 내 편집이 있는 행(I/U/D)은 덮어쓰지 않음: 내가 고친 칸은 그대로, 안 고친 칸만 서버 값으로,
 *    행에는 "서버 변경 있음" 표시(grid.markServerChanged). 서버에서 지운 행도 내 편집이 있으면 남기고 표시
 *  - 바뀐 칸은 강조(grid.flashCells), 포커스·스크롤·정렬은 applyTransaction(merge) 그대로 유지
 */
import { RowState, keyColsOf, keyString } from './dataset.js';
const same = (a, b) => (a == null && b == null) || a === b || (a != null && b != null && String(a) === String(b));
/** 이벤트 rows → 행 객체 배열 (컬럼형도 받음, 순수) */
export function eventRows(rows) {
    if (Array.isArray(rows))
        return rows;
    if (rows && typeof rows === 'object' && 'data' in rows) {
        const d = rows, out = [];
        for (let i = 0; i < d.n; i++) {
            const o = {};
            for (const k in d.data)
                o[k] = d.data[k][i] ?? null;
            out.push(o);
        }
        return out;
    }
    return [];
}
/**
 * 이벤트 한 건 반영 (EventSource 없이도 씀 — 웹소켓·폴링 등).
 * 충돌(내 편집이 있는 행)은 conflicts 로 돌려주고 grid 가 있으면 행에 표시한다.
 */
export function applyRowEvent(ds, ev, opts = {}) {
    const key = opts.key ?? ev.key ?? ds.keyCol;
    const res = { added: [], updated: [], removed: [], conflicts: [], flashed: [] };
    const kcs = keyColsOf(key);
    if (!key || !kcs)
        throw new Error('applyRowEvent: key 가 필요합니다');
    const isKey = new Set(kcs);
    const rows = eventRows(ev.rows);
    const add = [], upd = [], rem = [];
    const marks = [];
    const has = (c) => ds.hasColumn(c);
    ds.batch(() => {
        for (const o of rows) {
            const k = o && typeof o === 'object' ? keyString(o, kcs) : null; // 복합 키도 같은 문자열 (KEY_SEP)
            if (k == null)
                continue;
            const r = ds.findByKey(k, key);
            const st = r >= 0 ? ds.rowState(r) : -1;
            if (ev.type === 'remove') {
                if (r < 0 || st === RowState.DISCARDED)
                    continue;
                if (st === RowState.NORMAL)
                    rem.push(k);
                else {
                    res.conflicts.push(r);
                    marks.push([r, '서버에서 삭제된 행입니다 (저장 안 한 내 편집 있음)']);
                }
                continue;
            }
            if (r < 0 || st === RowState.DISCARDED) {
                add.push(o);
                continue;
            }
            const changed = Object.keys(o).filter(c => has(c) && !isKey.has(c) && !same(ds.get(r, c), o[c]));
            if (st === RowState.NORMAL) {
                if (changed.length) {
                    upd.push(o);
                    for (const c of changed)
                        res.flashed.push({ row: r, col: c });
                }
                continue;
            }
            // 내 편집이 있는 행: 내가 고친 칸은 두고 나머지만 서버 값 (상태·원본값은 그대로)
            // 새 행(I)은 원본이 없으므로 전부 내 값으로 봄
            const ins = st === RowState.INSERT;
            const mine = changed.filter(c => ins || ds.isCellChanged(r, c)), theirs = ins ? [] : changed.filter(c => !ds.isCellChanged(r, c));
            for (const c of theirs) {
                ds.setBase(r, c, o[c]);
                res.flashed.push({ row: r, col: c });
            }
            if (changed.length) {
                res.conflicts.push(r);
                marks.push([r, '서버 변경 있음' + (mine.length ? ` — 내가 고친 칸(${mine.join(', ')})은 그대로 두었습니다` : '')]);
            }
        }
    });
    if (add.length || upd.length || rem.length) {
        const t = ds.applyTransaction({ add, update: upd, remove: rem }, key);
        res.added = t.added;
        res.updated = t.updated;
        res.removed = t.removed;
        for (const r of t.added)
            res.flashed.push({ row: r });
    }
    const g = opts.grid;
    if (g) {
        for (const [r, m] of marks)
            g.markServerChanged(r, m);
        if (res.flashed.length)
            g.flashCells(res.flashed);
    }
    return res;
}
/**
 * 서버 이벤트(SSE) 구독 → applyRowEvent. 연결이 끊기면 EventSource 가 다시 붙는다.
 * opts.event: SSE 이벤트 이름 (기본 'rows'), onEvent: 반영 뒤 알림 (감사·상태 표시)
 */
export function subscribeRows(ds, url, opts = {}) {
    const es = new EventSource(url, { withCredentials: !!opts.withCredentials });
    let n = 0, opened = () => { };
    const ready = new Promise(res => { opened = res; });
    es.addEventListener('open', () => opened());
    es.addEventListener('ready', () => opened());
    es.addEventListener(opts.event ?? 'rows', (m) => {
        let ev;
        try {
            ev = JSON.parse(m.data);
        }
        catch {
            return;
        }
        n++;
        const r = applyRowEvent(ds, ev, { key: opts.key, grid: opts.grid });
        opts.onEvent?.(ev, r);
    });
    if (opts.onError)
        es.addEventListener('error', opts.onError);
    return { ready, close: () => es.close(), get count() { return n; } };
}
